import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import {
    Table,
    Typography,
    Modal,
    Button,
    Input,
    DatePicker,
    Select,
    Row,
    Col,
    Space,
    Spin,
} from "antd";
import ViewOneEmployeeRecord from "./hr/ViewOneEmployeeRecord";
import { CheckOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import "./TeamLeaves.css";
import ViewEmpDetails from "./ViewEmpDetails";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function Teamleaves() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const { authData, backUrl } = useContext(AuthContext);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showSingle, setShowSingle] = useState(false);
    const [showRejectReason, setShowRejectReason] = useState({ status: false, data: null });
    const [showAcceptReason, setShowAcceptReason] = useState({ status: false, data: null });
    const [rejectionData, setRejectionData] = useState("");
    const [approvalData, setApprovalData] = useState("");

    // Loading States
    const [loadingData, setLoadingData] = useState(false);
    const [loadingUnapproved, setLoadingUnapproved] = useState(false);

    // Bulk Action States
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showBulkApprove, setShowBulkApprove] = useState(false);
    const [showBulkReject, setShowBulkReject] = useState(false);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkProcessing, setBulkProcessing] = useState(false);

    const [isEmployeeView, setIsEmployeeView] = useState(
        localStorage.getItem("Hrselected") === "employee"
    );

    const [unapprovedLeaves, setUnapprovedLeaves] = useState([]);
    const [isUnapproved, setIsUnapproved] = useState(false);

    const [pagination, setPagination] = useState({ current: 1, total: 0 });

    const [isHod, setIsHod] = useState(false);

    useEffect(() => {
        if (authData?.employee?.is_hod === true) {
            setIsHod(true);
        }
    }, [authData]);

    const fetchUnapproved = async () => {
        setLoadingUnapproved(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/unapproved-leaves/?is_employee_view=true`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            setUnapprovedLeaves(res.data.results || []);
        } catch (error) {
            console.error("Error fetching unapproved leaves:", error);
        } finally {
            setLoadingUnapproved(false);
        }
    };

    useEffect(() => {
        fetchUnapproved();
    }, []);

    // Filters
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [empNameFilter, setEmpNameFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState([]);
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("Pending");

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || []);
    };

    const fetchDepartments = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results || []);
    };

    const fetchDesignations = async (deptId) => {
        const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDesignations(res.data.results || []);
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    const fetchData = async (page = 1) => {
        setLoadingData(true);
        try {
            const params = new URLSearchParams();
            params.append("is_employee_view", "true");
            params.append("page", page);

            if (approvalStatusFilter && !isUnapproved) {
                const statusMapping = {
                    "Pending": ["Pending", "Pending Cancellation", "Pending Approval"],
                    "Approved": ["Approved", "Cancellation Approved"],
                    "Rejected": ["Rejected", "Cancellation Rejected"]
                };

                const allowedStatuses = statusMapping[approvalStatusFilter] || [approvalStatusFilter];

                params.append("status", allowedStatuses.join(","));
            }

            const res = await axios.get(`${backUrl}/api/hr/leaves/?${params.toString()}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setData(res.data.results || []);
            setPagination(prev => ({
                ...prev,
                current: page,
                total: res.data.count ?? 0,
            }));

        } catch (error) {
            toast.error("Failed to fetch leave data");
        } finally {
            setLoadingData(false);
        }
    };

    // 2. Remove status filtering from applyFilters
    const applyFilters = () => {
        let temp = isUnapproved ? [...unapprovedLeaves] : [...data];

        if (empCodeFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.username?.toLowerCase().includes(empCodeFilter.toLowerCase())
            );
        }
        if (empNameFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.first_name?.toLowerCase().includes(empNameFilter.toLowerCase())
            );
        }
        if (organisationFilter) {
            temp = temp.filter(item => item.employee?.organisation_name === organisationFilter);
        }
        if (departmentFilter) {
            temp = temp.filter(item => item.employee?.department_name === departmentFilter);
        }
        if (designationFilter) {
            temp = temp.filter(item => item.employee?.designation_name === designationFilter);
        }
        if (dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            temp = temp.filter(item => {
                const startDate = dayjs(item.start_date);
                return (
                    startDate.isSameOrAfter(start, "day") &&
                    startDate.isSameOrBefore(end, "day")
                );
            });
        }

        // ⭐ Status filtering removed — handled by backend via query params

        setFilteredData(temp);
    };

    // 3. Re-fetch when approvalStatusFilter or isUnapproved changes
    useEffect(() => {
        fetchData(1);
    }, [approvalStatusFilter, isUnapproved]);

    useEffect(() => {
        applyFilters();
    }, [
        empCodeFilter,
        empNameFilter,
        dateRangeFilter,
        approvalStatusFilter,
        organisationFilter,
        departmentFilter,
        designationFilter,
        data,
        unapprovedLeaves,
        isUnapproved,
    ]);

    const [approving, setApproving] = useState(false);

    const handleTableChange = (pag) => {
        fetchData(pag.current);
    };

    const handleApprove = async (id) => {
        setApproving(true);
        const approveApi = `${backUrl}/api/hr/leaves/approve/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-leaves/${id}/admin-update-status/`;

        let res;
        try {
            if (isUnapproved === true) {
                res = await axios.post(
                    unApprovedApi,
                    { status: "Approved", approver_reason: approvalData },
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
            } else {
                res = await axios.post(
                    approveApi,
                    { leave_ids: [id], approver_reason: approvalData },
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
            }

            toast.success(res?.data?.message || res?.data?.detail || "Leave Approved Successfully");
            setApprovalData("");
            setShowAcceptReason({ status: false, data: null });
            await fetchData();
            await fetchUnapproved();
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Approval failed");
        } finally {
            setApproving(false);
        }
    };

    const [rejecting, setRejecting] = useState(false);

    const handleReject = async (id) => {
        setRejecting(true);
        const rejectApi = `${backUrl}/api/hr/leaves/reject/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-leaves/${id}/admin-update-status/`;

        let res;
        try {
            if (isUnapproved === true) {
                res = await axios.post(
                    unApprovedApi,
                    { status: "Rejected", rejection_reason: rejectionData },
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
            } else {
                res = await axios.post(
                    rejectApi,
                    { leave_ids: [id], rejection_reason: rejectionData },
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
            }

            toast.success(res.data?.message || "Leave Rejected Successfully");
            setRejectionData("");
            setShowRejectReason({ status: false, data: null });
            await fetchData();
            await fetchUnapproved();
        } catch (error) {
            const errData = error?.response?.data;
            let errorMsg = "Rejection failed";
            if (typeof errData === "string") errorMsg = errData;
            else if (errData?.error) errorMsg = errData.error;
            else if (errData?.detail) errorMsg = errData.detail;
            else if (typeof errData === "object") errorMsg = Object.values(errData).flat().join(", ");
            toast.error(errorMsg);
        } finally {
            setRejecting(false);
        }
    };

    const handleBulkApprove = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        setBulkProcessing(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/leaves/approve/?is_employee_view=true`,
                { leave_ids: selectedRowKeys, approver_reason: bulkRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Leaves approved successfully");
            setBulkRemarks("");
            setShowBulkApprove(false);
            setSelectedRowKeys([]);
            await fetchData();
            await fetchUnapproved();
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk approval failed");
        } finally {
            setBulkProcessing(false);
        }
    };

    const handleBulkReject = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        if (!bulkRemarks.trim()) { toast.warning("Please provide a reason for rejection"); return; }
        setBulkProcessing(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/leaves/reject/?is_employee_view=true`,
                { leave_ids: selectedRowKeys, rejection_reason: bulkRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Leaves rejected successfully");
            setBulkRemarks("");
            setShowBulkReject(false);
            setSelectedRowKeys([]);
            await fetchData();
            await fetchUnapproved();
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk rejection failed");
        } finally {
            setBulkProcessing(false);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: (record) => ({ name: record.id }),
    };

    // Derived loading state based on active tab
    const isLoading = isUnapproved ? loadingUnapproved : loadingData;

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "user", "username"],
            key: "employee_code",
            render: (text, record) => (
                <a onClick={() => { setSelectedEmployee(record); setShowSingle(true); }}>{text}</a>
            ),
            fixed: "left",
            width: 130,
        },
        {
            title: "Employee Name",
            key: "employee_name",
            render: (record) => {
                const firstName = record?.employee?.user?.first_name || "";
                const lastName = record?.employee?.user?.last_name || "";
                return `${firstName} ${lastName}`.trim() || "-";
            },
        },
        {
            title: "Approve",
            key: "approve",
            render: (_, record) => (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                    onClick={() => setShowAcceptReason({ status: true, data: record.id })}
                />
            ),
        },
        {
            title: "Reject",
            key: "reject",
            render: (_, record) => (
                <Button
                    danger
                    type="primary"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={() => setShowRejectReason({ status: true, data: record.id })}
                />
            ),
        },
        {
            title: "Leave Type",
            key: "leave_type",
            render: (_, record) => (
                <div>
                    <span>{record.leave_type}</span>
                    {(record.leave_type === "Compoff" || record.leave_type === "Weekoff") && record.work_dates?.length > 0 && (
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#1890ff" }}>
                            <strong>Work Dates:</strong> {record.work_dates.join(", ")}
                        </div>
                    )}
                </div>
            ),
        },
        { title: "Duration Type", dataIndex: "duration_type", key: "duration_type" },
        { title: "Days", dataIndex: "leave_days_count", key: "leave_days_count" },
        { title: "Start Date", dataIndex: "start_date", key: "start_date" },
        { title: "End Date", dataIndex: "end_date", key: "end_date" },
        {
            title: "Reason",
            key: "reason",
            render: (_, record) => (
                <>
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {record.reason}
                    </div>
                    <br /><br />
                    {record.cancellation_reason && (
                        <div style={{ marginTop: "4px", fontWeight: 600, color: "red", backgroundColor: "#ffe6e6", padding: "3px 6px", borderRadius: "6px", display: "inline-block" }}>
                            Cancellation Reason: {record.cancellation_reason}
                        </div>
                    )}
                </>
            ),
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                const status = record.status;
                return (
                    <span style={{
                        fontWeight: 500,
                        color: status === "Approved" || status === "Cancellation Approved" ? "green"
                            : status === "Rejected" || status === "Cancellation Rejected" ? "red"
                                : "#faad14",
                    }}>
                        {status}
                    </span>
                );
            },
        },
        {
            title: "Remarks",
            key: "remarks",
            width: 400,
            render: (_, record) => {
                if (isUnapproved) return record.remarks || "-";
                const approvals = record.approvals || [];
                if (!approvals.length) return "-";
                return (
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {approvals.map((a, index) => (
                            <div key={index} style={{ borderBottom: index < approvals.length - 1 ? "1px solid #ddd" : "none", paddingBottom: "6px", marginBottom: "6px" }}>
                                <strong>
                                    {a.approver?.first_name || a.approver?.last_name
                                        ? `${a.approver?.first_name} ${a.approver?.last_name}`
                                        : a.approver?.user?.username}:
                                </strong>{" "}
                                <span style={{ color: "green" }}>{a.approver_reason}</span>
                                <span style={{ color: "green" }}>{a.rejection_reason}</span>
                                <br /><br />
                                {a.cancellation_reason && (
                                    <div style={{ marginTop: "4px", fontWeight: 600, color: "red", backgroundColor: "#ffe6e6", padding: "3px 6px", borderRadius: "6px", display: "inline-block" }}>
                                        Cancellation Remarks: {a.cancellation_reason}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Document",
            dataIndex: "leave_document",
            key: "leave_document",
            render: (file) => file ? <a href={file} target="_blank" rel="noopener noreferrer">View</a> : "-",
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name" },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name" },

    ];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <center><Title level={5}>Team Leaves</Title></center>

            {/* Status Filter Buttons */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button
                    type={approvalStatusFilter === "Pending" && !isUnapproved ? "primary" : "default"}
                    onClick={() => { setApprovalStatusFilter("Pending"); setIsUnapproved(false); }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Pending" && !isUnapproved ? "#faad14" : undefined,
                        borderColor: approvalStatusFilter === "Pending" && !isUnapproved ? "#faad14" : undefined,
                    }}
                >
                    {approvalStatusFilter === "Pending" && !isUnapproved && loadingData
                        ? <><LoadingOutlined style={{ marginRight: 6 }} />Pending</>
                        : "Pending"}
                </Button>
                <Button
                    type={approvalStatusFilter === "Approved" && !isUnapproved ? "primary" : "default"}
                    onClick={() => { setApprovalStatusFilter("Approved"); setIsUnapproved(false); }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Approved" && !isUnapproved ? "#4CAF50" : undefined,
                        borderColor: approvalStatusFilter === "Approved" && !isUnapproved ? "#4CAF50" : undefined,
                    }}
                >
                    {approvalStatusFilter === "Approved" && !isUnapproved && loadingData
                        ? <><LoadingOutlined style={{ marginRight: 6 }} />Approved</>
                        : "Approved"}
                </Button>
                <Button
                    type={approvalStatusFilter === "Rejected" && !isUnapproved ? "primary" : "default"}
                    onClick={() => { setApprovalStatusFilter("Rejected"); setIsUnapproved(false); }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Rejected" && !isUnapproved ? "#f5222d" : undefined,
                        borderColor: approvalStatusFilter === "Rejected" && !isUnapproved ? "#f5222d" : undefined,
                    }}
                >
                    {approvalStatusFilter === "Rejected" && !isUnapproved && loadingData
                        ? <><LoadingOutlined style={{ marginRight: 6 }} />Rejected</>
                        : "Rejected"}
                </Button>

                {authData?.employee?.is_hod && (
                    <Button
                        type={isUnapproved ? "primary" : "default"}
                        onClick={() => { setApprovalStatusFilter("Unapproved"); setIsUnapproved(true); }}
                        style={{
                            backgroundColor: isUnapproved ? "blue" : undefined,
                            borderColor: isUnapproved ? "blue" : undefined,
                        }}
                    >
                        {isUnapproved && loadingUnapproved
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />Unapproved Leaves</>
                            : "Unapproved Leaves"}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="filters-container">
                <Row gutter={[12, 12]} justify="center">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input placeholder="Employee Code" value={empCodeFilter} onChange={e => setEmpCodeFilter(e.target.value)} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input placeholder="Employee Name" value={empNameFilter} onChange={e => setEmpNameFilter(e.target.value)} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: "100%" }}
                            value={organisationFilter || undefined}
                            allowClear
                            onChange={(value) => {
                                setOrganisationFilter(value);
                                setDepartmentFilter("");
                                setDesignationFilter("");
                                setDepartments([]);
                                setDesignations([]);
                                if (value) {
                                    const orgObj = Organisations.find(o => o.name === value);
                                    if (orgObj) fetchDepartments(orgObj.id);
                                }
                            }}
                        >
                            {Organisations.map(org => <Option key={org.id} value={org.name}>{org.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={departmentFilter || undefined}
                            allowClear
                            disabled={!organisationFilter}
                            onChange={(value) => {
                                setDepartmentFilter(value);
                                setDesignationFilter("");
                                setDesignations([]);
                                if (value) {
                                    const deptObj = departments.find(d => d.name === value);
                                    if (deptObj) fetchDesignations(deptObj.id);
                                }
                            }}
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {departments.map(dept => <Option key={dept.id} value={dept.name}>{dept.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={designationFilter || undefined}
                            allowClear
                            disabled={!departmentFilter}
                            onChange={(value) => setDesignationFilter(value)}
                            showSearch
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {designations.map(des => <Option key={des.id} value={des.name}>{des.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <RangePicker value={dateRangeFilter} onChange={dates => setDateRangeFilter(dates || [])} style={{ width: "100%" }} />
                    </Col>
                </Row>
            </div>

            {/* Bulk Action Buttons */}
            {selectedRowKeys.length > 0 && (
                <div style={{ margin: "20px 0", textAlign: "center" }}>
                    <Space>
                        <span style={{ fontWeight: "500" }}>
                            {selectedRowKeys.length} item{selectedRowKeys.length > 1 ? "s" : ""} selected
                        </span>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                            onClick={() => setShowBulkApprove(true)}
                        >
                            Bulk Approve
                        </Button>
                        <Button danger type="primary" icon={<CloseOutlined />} onClick={() => setShowBulkReject(true)}>
                            Bulk Reject
                        </Button>
                        <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                    </Space>
                </div>
            )}

            {/* Table */}
            <div className="table-responsive">
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        total: pagination.total,
                        pageSize: 25,         // match your backend page size
                        showSizeChanger: false,
                    }}
                    rowSelection={rowSelection}
                    loading={{
                        spinning: isLoading,
                        indicator: <LoadingOutlined style={{ fontSize: 36, color: "#0e497b" }} spin />,
                        tip: "Loading leaves...",
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                    scroll={{ x: "max-content" }}
                />
            </div>

            {/* Individual Reject Modal */}
            <Modal
                title="Reason for Rejection"
                open={showRejectReason.status}
                footer={null}
                onCancel={() => setShowRejectReason({ status: false, data: null })}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason here..."
                    value={rejectionData}
                    onChange={(e) => setRejectionData(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowRejectReason({ status: false, data: null })} disabled={rejecting}>Cancel</Button>
                    <Button type="primary" danger loading={rejecting} disabled={rejecting} onClick={() => handleReject(showRejectReason.data)}>
                        {rejecting ? "Rejecting..." : "Reject"}
                    </Button>
                </div>
            </Modal>

            {/* Individual Approve Modal */}
            <Modal
                title="Approval Remarks"
                open={showAcceptReason.status}
                footer={null}
                onCancel={() => setShowAcceptReason({ status: false, data: null })}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter remarks here (optional)..."
                    value={approvalData}
                    onChange={(e) => setApprovalData(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowAcceptReason({ status: false, data: null })} disabled={approving}>Cancel</Button>
                    <Button type="primary" loading={approving} disabled={approving} style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }} onClick={() => handleApprove(showAcceptReason.data)}>
                        {approving ? "Approving..." : "Approve"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal
                title={`Bulk Approve (${selectedRowKeys.length} leaves)`}
                open={showBulkApprove}
                footer={null}
                onCancel={() => { setShowBulkApprove(false); setBulkRemarks(""); }}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter common remarks for all selected leaves (optional)..."
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => { setShowBulkApprove(false); setBulkRemarks(""); }} disabled={bulkProcessing}>Cancel</Button>
                    <Button type="primary" loading={bulkProcessing} disabled={bulkProcessing} style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }} onClick={handleBulkApprove}>
                        {bulkProcessing ? "Approving..." : "Approve All"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal
                title={`Bulk Reject (${selectedRowKeys.length} leaves)`}
                open={showBulkReject}
                footer={null}
                onCancel={() => { setShowBulkReject(false); setBulkRemarks(""); }}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter common rejection reason for all selected leaves..."
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => { setShowBulkReject(false); setBulkRemarks(""); }} disabled={bulkProcessing}>Cancel</Button>
                    <Button type="primary" danger loading={bulkProcessing} disabled={bulkProcessing || !bulkRemarks.trim()} onClick={handleBulkReject}>
                        {bulkProcessing ? "Rejecting..." : "Reject All"}
                    </Button>
                </div>
            </Modal>

            {/* Employee Record Modal */}
            <Modal open={showSingle} footer={null} onCancel={() => setShowSingle(false)} width={800}>
                <ViewEmpDetails employee={selectedEmployee} />
            </Modal>
        </>
    );
}